import { hijriMonthId } from "../utils/hijri";
import { useState, useEffect } from "react";
import { useAuth, apiClient, useLanguage } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import PageHeader from "../components/layout/PageHeader";
import IslamicLoadingScreen from "../components/shared/IslamicLoadingScreen";
import { toast } from "sonner";
import {
  MapPin,
  Clock,
  Sunrise,
  Sunset,
  Moon,
  Sun,
  Calendar,
  Navigation,
  RefreshCw,
  Volume2,
  VolumeX,
  Sparkles,
  List,
  CalendarDays,
  Bell,
  Timer
} from "lucide-react";

const PrayerTimes = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isEnglish = language === "en";
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [location, setLocation] = useState(null);
  const [cityName, setCityName] = useState("");
  const [nextPrayer, setNextPrayer] = useState(null);
  const [countdownText, setCountdownText] = useState("");
  const [isTimeNow, setIsTimeNow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("today"); // 'today' or 'month'

  // Check if today is Friday
  const isFriday = new Date().getDay() === 5;

  // Determine if should show Friday prayer (Jumat)
  // Default to male (show Jumat) if gender is not set
  const shouldShowFridayPrayer = isFriday && (!user?.gender || user?.gender === "male");

  // Prayer names mapping
  const prayerNames = {
    Imsak: { id: "Imsak", en: "Imsak", icon: Moon },
    Fajr: { id: "Subuh", en: "Fajr", icon: Sunrise },
    Dhuhr: {
      id: shouldShowFridayPrayer ? "Jumat" : "Zuhur",
      en: shouldShowFridayPrayer ? "Friday" : "Dhuhr",
      icon: Sun
    },
    Asr: { id: "Ashar", en: "Asr", icon: Clock },
    Maghrib: { id: "Maghrib", en: "Maghrib", icon: Sunset },
    Isha: { id: "Isya", en: "Isha", icon: Moon },
  };

  // Get user location
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });
  };

  // Get city name from coordinates using OpenStreetMap Nominatim API
  const getCityName = async (lat, long) => {
    try {
      const lang = isEnglish ? "en" : "id";
      let response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${long}&localityLanguage=${lang}`
      );

      if (!response.ok) {
        response = await fetch(
          `https://geocode.maps.co/reverse?lat=${lat}&lon=${long}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();

      // Try to get city name from various possible fields (Indonesia-specific)
      const city =
        data.city ||
        data.locality ||
        data.principalSubdivision ||
        data.address?.city ||
        data.address?.town ||
        data.address?.village ||
        data.address?.suburb ||
        data.address?.county ||
        data.address?.district ||
        data.address?.region ||
        data.address?.province ||
        data.address?.state ||
        data.display_name?.split(",")[0] ||
        (isEnglish ? "Unknown Location" : "Lokasi Unknown");

      setCityName(city);
    } catch (error) {
      // Silent fail - this is normal if API is blocked/rate-limited
      console.warn("Could not fetch city name from API (this is normal)");
      setCityName(isEnglish ? "Your Location" : "Lokasi Anda");
    }
  };

  // Play adhan audio - temporarily disabled
  const playAdhan = (prayerName) => {
    // Temporarily disabled - will be implemented later
    console.log("Adhan clicked for:", prayerName);
  };

  // Fetch prayer times for today
  const fetchPrayerTimes = async (lat, long) => {
    try {
      const res = await apiClient.get("/prayer-times", {
        params: { latitude: lat, longitude: long },
      });
      setPrayerTimes(res.data);
      calculateNextPrayer(res.data.timings);
    } catch (error) {
      console.error("Error fetching prayer times:", error);
      toast.error(
        isEnglish
          ? "Failed to fetch prayer times"
          : "Gagal mengambil jadwal shalat"
      );
    }
  };

  // Fetch monthly calendar
  const fetchCalendar = async (lat, long) => {
    try {
      const res = await apiClient.get("/prayer-times/calendar", {
        params: { latitude: lat, longitude: long },
      });
      setCalendar(res.data.calendar);
    } catch (error) {
      console.error("Error fetching calendar:", error);
      toast.error(
        isEnglish
          ? "Failed to fetch calendar"
          : "Gagal mengambil kalender"
      );
    }
  };

  const parseHHMM = (str) => {
    if (!str) return null;
    const [h, m] = str.slice(0, 5).split(":").map((x) => parseInt(x, 10));
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  };

  const calculateNextPrayer = (timings) => {
    const order = ["Imsak", "Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
    const now = new Date();
    let found = null;
    for (const name of order) {
      const t = timings?.[name];
      const target = parseHHMM(t);
      if (!target) continue;
      if (target > now) {
        found = { name, time: t, target, isTomorrow: false };
        break;
      }
    }
    if (!found && timings?.Fajr) {
      const t = timings.Fajr;
      const target = parseHHMM(t);
      if (target) target.setDate(target.getDate() + 1);
      found = { name: "Fajr", time: t, target, isTomorrow: true };
    }
    setNextPrayer(found);
  };

  const formatDiff = (ms, isEnglish) => {
    if (ms <= 0) return isEnglish ? "Time to pray" : "Saatnya Shalat";
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const hh = h > 0 ? String(h).padStart(2, "0") + ":" : "";
    return `${hh}${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // Initialize
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const loc = await getLocation();
        setLocation(loc);

        await getCityName(loc.latitude, loc.longitude);

        await Promise.all([
          fetchPrayerTimes(loc.latitude, loc.longitude),
          fetchCalendar(loc.latitude, loc.longitude),
        ]);
      } catch (error) {
        console.error("Error getting location:", error);
        toast.error(
          isEnglish
            ? "Please enable location access"
            : "Mohon izinkan akses lokasi"
        );
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  // Update next prayer every minute
  useEffect(() => {
    if (prayerTimes) {
      const interval = setInterval(() => {
        calculateNextPrayer(prayerTimes.timings);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [prayerTimes]);

  useEffect(() => {
    if (!nextPrayer?.target) return;
    const tick = () => {
      const now = new Date();
      const diff = nextPrayer.target - now;
      setCountdownText(formatDiff(diff, isEnglish));
      setIsTimeNow(diff <= 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextPrayer, isEnglish]);

  // Handle refresh
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const loc = location || await getLocation();
      setLocation(loc);

      await getCityName(loc.latitude, loc.longitude);

      await Promise.all([
        fetchPrayerTimes(loc.latitude, loc.longitude),
        fetchCalendar(loc.latitude, loc.longitude),
      ]);
      toast.success(
        isEnglish ? "Prayer times updated" : "Jadwal shalat diperbarui"
      );
    } catch (error) {
      console.error("Error refreshing:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <IslamicLoadingScreen
        title={isEnglish ? "Loading Prayer Times" : "Memuat Jadwal Shalat"}
        message={isEnglish ? "Subhanallah... While waiting, let's prepare for prayer..." : "Subhanallah... Sambil menunggu, siapkan diri untuk shalat..."}
        language={isEnglish ? "en" : "id"}
        size="large"
      />
    );
  }

  const prayerOrder = ["Imsak", "Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];

  // Get current time stats
  const getCurrentPrayerIndex = () => {
    if (!nextPrayer) return -1;
    const idx = prayerOrder.indexOf(nextPrayer.name);
    return nextPrayer.isTomorrow ? -1 : idx;
  };

  const getTabBadge = (tabValue) => {
    if (tabValue === "today") return 1;
    if (tabValue === "month") return calendar.length;
    return 0;
  };

  return (
    <div className="min-h-screen" data-testid="prayer-times-page">
      <PageHeader
        title={isEnglish ? "Prayer Times" : "Jadwal Shalat"}
        rightContent={
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={loading}
            className="rounded-xl"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        }
      />

      <div className="page-container space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <MapPin className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-sm md:text-lg font-bold truncate">{cityName || "..."}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Location" : "Lokasi"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Timer className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-lg md:text-2xl font-bold">{countdownText || "--:--"}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Next Prayer" : "Shalat Berikutnya"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Calendar className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-sm md:text-lg font-bold">{calendar.length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Days This Month" : "Hari Bulan Ini"}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 border border-primary/20 dark:border-primary/40">
            <CardContent className="p-2 md:p-4 text-center">
              <Bell className="w-5 h-5 md:w-8 md:h-8 mx-auto mb-1 md:mb-2 text-primary" />
              <p className="text-lg md:text-2xl font-bold">{prayerOrder.length}</p>
              <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Daily Prayers" : "Shalat Harian"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Location Card with Premium Design */}
        <Card className="islamic-gradient text-white">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-center gap-3 md:gap-6">
              <div className="w-14 h-14 md:w-20 md:h-20 rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg flex-shrink-0">
                <MapPin className="w-7 h-7 md:w-10 md:h-10 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm text-white/80 font-medium">
                  {isEnglish ? "Your Location" : "Lokasi Anda"}
                </p>
                <p className="text-base md:text-2xl font-bold mt-0.5 md:mt-1 truncate">
                  {cityName || (isEnglish ? "Detecting location..." : "Mendeteksi lokasi...")}
                </p>
                {prayerTimes?.date?.hijri && prayerTimes?.date?.readable && (
                  <p className="text-xs md:text-sm text-white/90 font-semibold mt-1 md:mt-2">
                    {prayerTimes.date.hijri.day} {hijriMonthId(prayerTimes.date.hijri.month)} {prayerTimes.date.hijri.year} H
                  </p>
                )}
                {location && (
                  <p className="text-[10px] md:text-xs text-white/70 mt-0.5 md:mt-1">
                    {location.latitude.toFixed(4)}° N, {location.longitude.toFixed(4)}° E
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Prayer Alert - Premium Design */}
        {nextPrayer && (
          <Card className="border-none shadow-xl bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between gap-2 md:gap-4">
                <div className="flex items-center gap-2 md:gap-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg flex-shrink-0">
                    <Navigation className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] md:text-sm text-white/80 font-medium">
                      {isEnglish ? "Next Prayer" : "Shalat Berikutnya"}
                    </p>
                    <p className="text-base md:text-2xl font-bold text-white mt-0.5 md:mt-1">
                      {isEnglish
                        ? prayerNames[nextPrayer.name].en
                        : prayerNames[nextPrayer.name].id}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-2xl md:text-4xl font-bold tracking-wide ${isTimeNow ? "text-yellow-300 animate-pulse" : "text-white"}`}>
                    {countdownText || nextPrayer.time}
                  </p>
                  <p className="text-[10px] md:text-sm text-white/80 mt-0.5 md:mt-1">
                    {isEnglish ? `At ${nextPrayer.time?.slice(0, 5)}` : `Jam ${nextPrayer.time?.slice(0, 5)}`}
                  </p>
                  {isTimeNow && (
                    <p className="text-[10px] md:text-sm text-white/90 font-semibold animate-pulse">
                      {isEnglish ? "It's time to pray" : "Saatnya Shalat"}
                    </p>
                  )}
                  {nextPrayer.isTomorrow && (
                    <p className="text-[10px] md:text-sm text-white/80 font-medium mt-0.5 md:mt-1">
                      {isEnglish ? "Tomorrow" : "Besok"}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Premium Tabs */}
        <Card className="overflow-hidden">
          {/* Premium Tab Header */}
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background p-1">
            <div className="flex gap-1">
              {[
                { value: "today", label: isEnglish ? "Hari Ini" : "Hari Ini", icon: Clock, badge: getTabBadge("today") },
                { value: "month", label: isEnglish ? "Bulanan" : "Bulanan", icon: CalendarDays, badge: getTabBadge("month") }
              ].map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.value;

                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`flex-1 relative flex items-center justify-center gap-1 md:gap-2 px-2 md:px-3 py-2 md:py-3 rounded-xl font-medium text-xs md:text-sm transition-all duration-300 ${
                      isActive
                        ? "bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg shadow-primary/25"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    }`}
                  >
                    {isActive && (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/50 to-transparent rounded-xl animate-pulse" />
                    )}
                    <Icon className={`w-4 h-4 ${isActive ? "text-white" : ""}`} />
                    <span className="relative">{tab.label}</span>
                    {tab.badge !== null && tab.badge > 0 && (
                      <span
                        className={`ml-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          isActive
                            ? "bg-white/20 text-white border-white/30"
                            : "bg-primary/10 text-primary border-primary/20"
                        }`}
                      >
                        {tab.badge}
                      </span>
                    )}
                    {isActive && (
                      <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <CardContent className="p-3 md:p-6">
            {/* Today's Prayer Times */}
            {activeTab === "today" && prayerTimes && (
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-base md:text-lg font-bold">
                    {isEnglish ? "Today's Schedule" : "Jadwal Hari Ini"}
                  </h3>
                  <Badge variant="secondary" className="text-xs md:text-sm px-3 md:px-4 py-1 md:py-1.5 rounded-full">
                    {prayerTimes.date?.readable}
                  </Badge>
                </div>
                {prayerOrder.map((prayerName) => {
                  const prayer = prayerNames[prayerName];
                  const Icon = prayer.icon;
                  const time = prayerTimes.timings[prayerName];
                  const isNext = nextPrayer?.name === prayerName;

                  return (
                    <Card
                      key={prayerName}
                      className={`cursor-pointer hover:shadow-lg hover:scale-[1.01] transition-all duration-300 group ${
                        isNext
                          ? "bg-gradient-to-r from-primary/20 to-primary/10 border-2 border-primary/30 shadow-lg shadow-primary/10"
                          : ""
                      }`}
                    >
                      <CardContent className="p-3 md:p-5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 md:gap-4">
                            <div
                              className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 flex-shrink-0 ${
                                isNext
                                  ? "bg-gradient-to-br from-primary to-primary/70"
                                  : "bg-gradient-to-br from-primary/20 to-primary/10"
                              }`}
                            >
                              <Icon className={`w-5 h-5 md:w-7 md:h-7 ${isNext ? "text-white" : "text-primary"}`} />
                            </div>
                            <div>
                              <p className={`font-bold text-sm md:text-lg ${isNext ? "text-primary" : ""}`}>
                                {isEnglish ? prayer.en : prayer.id}
                              </p>
                              {isNext && (
                                <Badge variant="default" className="mt-1 text-[10px] md:text-xs bg-primary">
                                  {isEnglish ? "Next Prayer" : "Shalat Berikutnya"}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                            <p className={`text-lg md:text-2xl font-bold tracking-wide ${isNext ? "text-primary" : ""}`}>
                              {time?.slice(0, 5)}
                            </p>
                            <Button
                              variant={isNext ? "default" : "ghost"}
                              size="icon"
                              className="h-8 w-8 md:h-10 md:w-10 rounded-lg md:rounded-xl"
                              onClick={() => playAdhan(prayerName)}
                              title={isEnglish ? "Play Adhan" : "Putar Adzan"}
                            >
                              <Volume2 className="w-4 h-4 md:w-5 md:h-5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Monthly Calendar */}
            {activeTab === "month" && calendar.length > 0 && (
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-base md:text-lg font-bold">
                    {isEnglish ? "Monthly Calendar" : "Kalender Bulanan"}
                  </h3>
                  <Badge variant="secondary" className="text-xs md:text-sm px-3 md:px-4 py-1 md:py-1.5 rounded-full">
                    {calendar.length} {isEnglish ? "days" : "hari"}
                  </Badge>
                </div>
                <div className="max-h-[400px] md:max-h-[600px] overflow-y-auto pr-1 md:pr-2 space-y-2 md:space-y-3">
                  {calendar.map((day, index) => {
                    const date = new Date(day.date.timestamp * 1000);
                    const isToday = new Date().toDateString() === date.toDateString();

                    return (
                      <Card
                        key={index}
                        className={`hover:shadow-md transition-all duration-300 ${
                          isToday
                            ? "border-2 border-primary/40 bg-gradient-to-r from-primary/15 to-primary/5 shadow-lg shadow-primary/10"
                            : ""
                        }`}
                      >
                        <CardContent className="p-2 md:p-4">
                          <div className="flex items-center justify-between mb-2 md:mb-3">
                            <p className={`text-xs md:text-sm font-bold ${isToday ? "text-primary" : ""}`}>
                              {day.date.readable}
                            </p>
                            {isToday && (
                              <Badge variant="default" className="text-[10px] md:text-xs px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-primary">
                                {isEnglish ? "Today" : "Hari Ini"}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1 md:gap-2 text-[10px] md:text-xs">
                            {prayerOrder.map((prayerName) => {
                              const prayer = prayerNames[prayerName];
                              return (
                                <div
                                  key={prayerName}
                                  className={`text-center p-1.5 md:p-2 rounded-lg ${
                                    isToday ? "bg-primary/10" : "bg-muted/50"
                                  }`}
                                >
                                  <p className="text-muted-foreground mb-0.5 md:mb-1 font-medium text-[8px] md:text-[10px] uppercase tracking-wide">
                                    {prayer.id}
                                  </p>
                                  <p className="font-bold text-[10px] md:text-sm">
                                    {day.timings[prayerName]?.slice(0, 5)}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="bg-muted/50">
          <CardContent className="p-2 md:p-4">
            <p className="text-[10px] md:text-sm text-muted-foreground text-center">
              {isEnglish
                ? "Prayer times calculated using Kemenag Indonesia method"
                : "Jadwal shalat dihitung menggunakan metode Kemenag Indonesia"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrayerTimes;
